/**
 * In-place document preview, shared by every screen that owns documents.
 *
 * The point is to read an attached policy or ID card without first putting a copy of it in
 * the operator's Downloads folder — both because that is three extra steps to answer one
 * question on a phone call, and because an insurance office accumulating stray copies of
 * customers' identity documents on disk is a liability nobody asked for.
 *
 * Two constraints shaped the implementation.
 *
 * 1. **The file cannot be addressed directly.** The access token lives in memory and rides
 *    on an Authorization header, so `<iframe src="/api/v1/files/…">` sends no credentials
 *    and gets a 401. `openInline` pulls the bytes through the authenticated client and
 *    yields an object URL, which keeps the permission check exactly where it was.
 *
 * 2. **Only PDFs get an iframe.** An object URL inherits the origin of the document that
 *    created it, so anything scriptable rendered in a frame here would execute as this
 *    app — and `image/svg+xml` is scriptable. Images therefore go through `<img>`, which
 *    never runs script regardless of the payload, and the frame is reserved for
 *    `application/pdf`, handled by the browser's own viewer. Everything else is declared
 *    unpreviewable and offered as a download rather than guessed at.
 */

import * as React from 'react';
import { Download, FileText, TriangleAlert, WifiOff } from 'lucide-react';
import type { DocumentDto } from '@ias/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/misc';
import { ApiRequestError, download, openInline } from '@/lib/api/client';
import type { DocumentScope } from '@/lib/api/domains';
import { useOffline } from '@/lib/offline/context';
import { useI18n } from '@/lib/i18n';
import { formatBytes } from '@/lib/utils';

/**
 * What a MIME type can be shown as. Deliberately a closed allow-list rather than a
 * "not obviously binary" check — see rule 2 in the file header.
 */
export function previewKind(mimeType: string | null | undefined): 'pdf' | 'image' | 'none' {
  if (!mimeType) return 'none';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType.startsWith('image/')) return 'image';
  return 'none';
}

/** True when the list should offer a preview button at all. */
export function isPreviewable(document: DocumentDto): boolean {
  return previewKind(document.mimeType) !== 'none';
}

/**
 * One member per status rather than `status: 'idle' | 'loading'` on a shared member: a
 * discriminant that is itself a union does not narrow through an `||`, so the collapsed
 * version left `state.url` unreachable in the ready branch.
 */
type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; url: string; mimeType: string }
  /** `message: null` means "no server-supplied reason" — translated at render. */
  | { status: 'error'; message: string | null };

export function DocumentPreviewDialog({
  scope,
  doc,
  open,
  onOpenChange,
}: {
  scope: DocumentScope;
  doc: DocumentDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, dateTime } = useI18n();
  const { isOnline } = useOffline();

  const [state, setState] = React.useState<PreviewState>({ status: 'idle' });

  React.useEffect(() => {
    if (!open || !isOnline) return;

    let objectUrl: string | null = null;
    let cancelled = false;
    setState({ status: 'loading' });

    void (async () => {
      try {
        const result = await openInline(`/files/${scope}/${doc.id}`);
        objectUrl = result.url;

        // Closed, or switched to another file, while the bytes were in flight. Revoke now
        // rather than hand a URL to a viewer that will never mount to clean it up.
        if (cancelled) {
          URL.revokeObjectURL(result.url);
          return;
        }

        setState({ status: 'ready', url: result.url, mimeType: result.mimeType });
      } catch (error) {
        if (cancelled) return;
        setState({
          status: 'error',
          message: error instanceof ApiRequestError ? error.message : null,
        });
      }
    })();

    // Revoking on close is not housekeeping — an object URL holds its blob for the
    // lifetime of the document, and these are whole PDFs. Browsing ten attachments
    // without this leaks all ten.
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, isOnline, scope, doc.id]);

  // The fetched type wins over the stored one: the bytes are what will actually render.
  const kind = previewKind(state.status === 'ready' ? state.mimeType : doc.mimeType);

  const downloadButton = (
    <Button variant="outline" onClick={() => void download(`/files/${scope}/${doc.id}`, doc.fileName)}>
      <Download /> {t('action.download')}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle className="truncate" title={doc.fileName}>
            {doc.fileName}
          </DialogTitle>
          <DialogDescription>
            {doc.fileSize ? `${formatBytes(doc.fileSize)} · ` : ''}
            {dateTime(doc.uploadedAt)}
            {doc.uploadedByName ? ` · ${doc.uploadedByName}` : ''}
          </DialogDescription>
        </DialogHeader>

        {/*
          Tighter gutters than the default `px-6 py-5` below `sm`. A viewer's job is to give
          the document as much area as it can: on a 390px phone the standard padding spent
          48px of 358 on empty margin, and a scanned policy is exactly the content that
          cannot afford it. Restored at `sm`, where the room is not contested.
        */}
        <DialogBody className="px-3 py-3 sm:px-6 sm:py-5">
          {!isOnline ? (
            <Notice
              icon={<WifiOff className="text-serious size-5 shrink-0" />}
              title={t('offline.requiresConnection')}
              hint={t('document.previewNeedsConnection')}
            />
          ) : state.status === 'loading' || state.status === 'idle' ? (
            <Skeleton className="h-[70vh] w-full" />
          ) : state.status === 'error' ? (
            <Notice
              icon={<TriangleAlert className="text-serious size-5 shrink-0" />}
              title={t('error.generic')}
              hint={state.message ?? undefined}
            />
          ) : kind === 'pdf' ? (
            <iframe
              src={state.url}
              title={doc.fileName}
              className="border-border h-[70vh] w-full rounded-lg border"
            />
          ) : kind === 'image' ? (
            // grid+place-items rather than object-contain alone, so a small image sits
            // centred in the viewport instead of pinned to the top-left of it.
            <div className="border-border bg-muted/40 grid h-[70vh] w-full place-items-center overflow-auto rounded-lg border p-3">
              <img src={state.url} alt={doc.fileName} className="max-h-full max-w-full object-contain" />
            </div>
          ) : (
            <Notice
              icon={<FileText className="text-muted-foreground size-5 shrink-0" />}
              title={t('document.previewUnsupported')}
              hint={doc.mimeType ?? undefined}
            />
          )}
        </DialogBody>

        <DialogFooter>
          {downloadButton}
          <Button onClick={() => onOpenChange(false)}>{t('action.close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The three non-viewer states share one shape, so they share one component. */
function Notice({
  icon,
  title,
  hint,
}: {
  icon: React.ReactNode;
  title: string;
  hint?: string;
}) {
  return (
    <div className="border-border grid h-[40vh] w-full place-items-center rounded-lg border border-dashed px-6 text-center">
      <div className="flex flex-col items-center gap-2">
        {icon}
        <p className="text-sm font-medium">{title}</p>
        {hint && <p className="text-muted-foreground max-w-prose text-[13px]">{hint}</p>}
      </div>
    </div>
  );
}
